import { IntroOutroType } from 'components/VideoTemplateEditor/types';
import { IVideoUpload } from 'redux/middleware/api/media-upload-service';
import { updateState } from '../../shared/mutations';
import { VideoTemplateStateContent } from '../../types';

/**
 * Adds an intro/outro clip depending on the type provided. It requires a fully loaded
 * clip with its source object
 * @param {object} state - current template editor state
 * @param {string} type - clip to add type: intro | outro
 * @param {object} src - selected video clip upload object
 * @param {string} fileName - Uploaded clip's file name
 * @returns {object} - Updated state
 */
export const addIntroOutro = (
  state: VideoTemplateStateContent,
  type: IntroOutroType,
  src: IVideoUpload,
  fileName: string,
): VideoTemplateStateContent => {
  return updateState(state, {
    introOutro: {
      ...state.introOutro,
      [type]: {
        id: src.id,
        fileName,
        thumbnailUrl: src.previewThumbnail?.thumbnails?.[0]?.url,
        loaded: true,
      },
    },
  });
};

/**
 * Replaces a fully loaded intro/outro clip entry.
 * @param {object} state - current template editor state
 * @param {string} type - clip to replace type: intro | outro
 * @param {object} src - selected video clip upload object
 * @param {string} fileName - Uploaded clip's file name
 * @returns {object} - Updated state
 */
export const replaceIntroOutro = (
  state: VideoTemplateStateContent,
  type: IntroOutroType,
  src: IVideoUpload,
  fileName: string,
): VideoTemplateStateContent => {
  return updateState(state, {
    introOutro: {
      [type]: {
        id: src.id,
        fileName,
        thumbnailUrl: src.previewThumbnail?.thumbnails?.[0]?.url,
        loaded: true,
      },
    },
  });
};

/**
 * Removes a intro/outro clip element from the template editor state
 * @param {object} state - current template editor state
 * @param {string} type - clip to replace type: intro | outro
 * @returns {object} - Updated state
 */
export const deleteIntroOutro = (
  state: VideoTemplateStateContent,
  type: IntroOutroType,
): VideoTemplateStateContent => {
  return updateState(state, {
    introOutro: {
      [type]: undefined,
    },
  });
};

/**
 * Sets a not loaded intro/outro clip as loaded and completes its data
 * @param {object} state - current template editor state
 * @param {string} type - clip to set as loaded type: intro | outro
 * @param {object} src - loaded video clip upload object
 * @param {string} fileName - loaded clip's file name
 * @returns {object} - Updated state
 */
export const loadIntroOutroData = (
  state: VideoTemplateStateContent,
  type: IntroOutroType,
  src: IVideoUpload,
  fileName: string,
): VideoTemplateStateContent => {
  return updateState(state, {
    introOutro: {
      ...state.introOutro,
      [type]: {
        fileName,
        thumbnailUrl: src.previewThumbnail?.thumbnails?.[0]?.url,
        loaded: true,
      },
    },
  });
};

/**
 * Side utility for parsing the initial intro/outro state. If it is an url that
 * that was previously loaded, it will copy current state. Otherwise it will generate
 * a brand new entry
 * @param {object} currState - Current intro/outro editor state
 * @param {string} [currUrl] - Current default intro/outro clip url.
 * @returns {object | undefined} - The parsed intro/outro state for editor
 */
const getIntroOutroInitialState = (
  currState:
    | VideoTemplateStateContent['introOutro']['intro']
    | VideoTemplateStateContent['introOutro']['outro'],
  currUrl?: string,
):
  | VideoTemplateStateContent['introOutro']['intro']
  | VideoTemplateStateContent['introOutro']['outro'] => {
  if (currState?.url === currUrl) {
    return currState;
  }

  return currUrl ? { loaded: false, url: currUrl } : undefined;
};

/**
 * Sets intro/outro clips default state from prefs. The pref elements wont be set
 * if there is no pref set for the particular type. Also, selected clips will only
 * contain its url, but its loaded state will be set to false.
 * @param {object} state - current template editor state
 * @param {object} introOutro - Object containing introDefaultVideoUrl and outroDefaultVideoUrl (both optionals)
 * @param {object} currVideoTemplateState - Current editor state. It is used for carrying the previous intro/outro url when switching templates
 * @returns {object} - Updated state
 */
export const setIntroOutroDefaultPrefs = (
  state: VideoTemplateStateContent,
  introOutro: {
    introDefaultVideoUrl?: string;
    outroDefaultVideoUrl?: string;
  },
  currVideoTemplateState?: VideoTemplateStateContent,
): VideoTemplateStateContent => {
  const { introDefaultVideoUrl, outroDefaultVideoUrl } = introOutro;

  const intro = getIntroOutroInitialState(
    currVideoTemplateState?.introOutro?.intro,
    introDefaultVideoUrl,
  );
  const outro = getIntroOutroInitialState(
    currVideoTemplateState?.introOutro?.outro,
    outroDefaultVideoUrl,
  );

  return updateState(state, {
    introOutro: { intro, outro },
  });
};
